import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  PersonAdd as JoinIcon,
  PersonRemove as LeaveIcon,
  ArrowBack as BackIcon,
  MenuBook as StoryIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  EmojiEvents as TrophyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventsService, storyService } from '../services/api';
import ReactMarkdown from 'react-markdown';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [eventStories, setEventStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  const loadAllParticipants = async () => {
    try {
      setParticipantsLoading(true);
      const response = await eventsService.getEventParticipants(id, { limit: 100 });
      setParticipants(response.data.data.participants || []);
    } catch (err) {
      console.error('Error loading participants:', err);
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadEventInfo();
      loadEventSubmissions();
      //loadAllParticipants();
    }
  }, [id]);

  useEffect(() => {
    // No need to load participants again if we already have them
    if (participantsModalOpen && participants.length === 0 && id) {
      loadAllParticipants();
    }
  }, [participantsModalOpen, id]);

  const loadEventInfo = async () => {
    try {
      setLoading(true);
      const response = await eventsService.getEventInfo(id);
      console.log('Event info API response:', response);
      console.log('Event participant_count:', response.data.participant_count);
      setEvent(response.data.data);
      await loadAllParticipants();
    } catch (err) {
      setError('Failed to load event details');
      console.error('Error loading event info:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEventSubmissions = async () => {
    try {
      setStoriesLoading(true);
      const response = await eventsService.getEventSubmissions(id, { limit: 20 });
      console.log('Event submissions API response:', response);
      setEventStories(response.data.data.stories || []);
    } catch (err) {
      console.error('Error loading event submissions:', err);
    } finally {
      setStoriesLoading(false);
    }
  };

  const loadEventParticipants = async () => {
    try {
      const response = await eventsService.getEventParticipants(id, { limit: 50 });
      console.log('Event participants API response:', response);
      return response.data.data.participants || [];
    } catch (err) {
      console.error('Error loading event participants:', err);
      return [];
    }
  };

  const handleJoinEvent = async () => {
    if (!isAuthenticated) {
      setError('Please sign in to join events');
      return;
    }

    try {
      setActionLoading(true);
      await eventsService.joinEvent(id);
      setSuccess('Successfully joined the event!');
      loadEventInfo(); // Refresh to get updated participation status
    } catch (err) {
      setError('Failed to join event. Please try again.');
      console.error('Error joining event:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeaveEvent = async () => {
    try {
      setActionLoading(true);
      await eventsService.leaveEvent(id);
      setSuccess('Successfully left the event');
      loadEventInfo(); // Refresh to get updated participation status
    } catch (err) {
      setError('Failed to leave event. Please try again.');
      console.error('Error leaving event:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'warning';
      case 'active': return 'success';
      case 'closed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${end.getFullYear()}`;
  };

  // Helper functions for time display
  const getTimeUntilEnd = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'ended';
    if (diffDays === 0) return 'ends today';
    if (diffDays === 1) return 'ends tomorrow';
    return `${diffDays} days left`;
  };

  const getTimeUntilStart = (startDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'started';
    if (diffDays === 0) return 'starts today';
    if (diffDays === 1) return 'starts tomorrow';
    return `starts in ${diffDays} days`;
  };

  const getTimeSinceEnd = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = now - end;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'ended today';
    if (diffDays === 1) return 'ended yesterday';
    if (diffDays < 7) return `ended ${diffDays} days ago`;
    return `ended ${Math.floor(diffDays / 7)} weeks ago`;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleViewAllParticipants = () => {
    setParticipantsModalOpen(true);
  };

  const getParticipantDisplayName = (participant) => {
    // Check if participant has direct name fields (from API)
    if (participant.given_name && participant.family_name) {
      return `${participant.given_name} ${participant.family_name}`;
    } else if (participant.given_name) {
      return participant.given_name;
    }
    
    // Check if participant has user data with names (legacy format)
    if (participant.user) {
      const { given_name, family_name, email } = participant.user;
      if (given_name && family_name) {
        return `${given_name} ${family_name}`;
      } else if (given_name) {
        return given_name;
      } else if (email) {
        return email.split('@')[0]; // Use email username part
      }
    }
    
    // Fallback to participant email or display_name if available
    if (participant.display_name) {
      return participant.display_name;
    } else if (participant.email) {
      return participant.email.split('@')[0];
    }
    
    return 'Anonymous User';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Event not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/events')}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>

      <Grid container spacing={4}>
        {/* Main Event Details */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* Event Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <EventIcon />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" component="h1" gutterBottom>
                    {event.title}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      label={(event.status || 'unknown').toUpperCase()}
                      color={getStatusColor(event.status)}
                    />
                    {event.theme && (
                      <Chip
                        label={event.theme}
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon />
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </Typography>
              </Box>

              {/* Submission Guidelines */}
              {event.submission_guidelines && event.submission_guidelines.trim() && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📋 Submission Guidelines
                  </Typography>
                  <ReactMarkdown
                    components={{
                      // Clean, professional styling for markdown elements
                      h1: ({children}) => <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>{children}</Typography>,
                      h2: ({children}) => <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>{children}</Typography>,
                      h3: ({children}) => <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{children}</Typography>,
                      p: ({children}) => <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.6 }}>{children}</Typography>,
                      ul: ({children}) => <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>{children}</Box>,
                      ol: ({children}) => <Box component="ol" sx={{ pl: 2, mb: 1.5 }}>{children}</Box>,
                      li: ({children}) => <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.6 }}>{children}</Typography>,
                      strong: ({children}) => <Typography component="strong" sx={{ fontWeight: 700 }}>{children}</Typography>,
                      em: ({children}) => <Typography component="em" sx={{ fontStyle: 'italic' }}>{children}</Typography>,
                      code: ({children}) => <Typography component="code" sx={{ 
                        bgcolor: 'grey.200', 
                        px: 0.5, 
                        py: 0.25, 
                        borderRadius: 0.5, 
                        fontFamily: 'monospace',
                        fontSize: '0.875em'
                      }}>{children}</Typography>,
                      blockquote: ({children}) => <Box sx={{ 
                        borderLeft: '4px solid #666', 
                        pl: 2, 
                        py: 1, 
                        bgcolor: 'grey.100',
                        mb: 1.5,
                        fontStyle: 'italic'
                      }}>{children}</Box>
                    }}
                  >
                    {event.submission_guidelines}
                  </ReactMarkdown>
                </Box>
              )}

              {/* Event Submissions */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1
                }}>
                  <StoryIcon />
                  Event Submissions ({eventStories.length})
                </Typography>
                
                {storiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : eventStories.length > 0 ? (
                  <Grid container spacing={3}>
                    {eventStories.map((story) => (
                      <Grid item xs={12} key={story.story_id}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 4
                            },
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}
                          onClick={() => navigate(`/story/${story.story_id}`)}
                        >
                          <CardContent sx={{ p: 3 }}>
                            {/* Story Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {story.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon fontSize="small" />
                                    {/* by {story.author ? `${story.author.given_name} ${story.author.family_name}`.trim() || 'Anonymous' : 'Anonymous'} */}
                                    by {story.author_name ? `${story.author_name}`.trim() || 'Anonymous' : 'Anonymous'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    👁️ {story.view_count || 0} views
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    💬 {story.comment_count || 0} comments
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    📅 {new Date(story.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Story Preview */}
                            {story.summary && (
                              <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                {story.summary}
                              </Typography>
                            )}
                            
                            {/* Story Content Preview */}
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                mb: 2,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.6
                              }}
                            >
                              {story.content?.substring(0, 300)}...
                            </Typography>

                            {/* Story Stats */}
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              pt: 2,
                              borderTop: '1px solid',
                              borderColor: 'grey.100'
                            }}>
                              <Box sx={{ display: 'flex', gap: 3 }}>
                                {story.word_count && (
                                  <Typography variant="body2" color="text.secondary">
                                    📝 {story.word_count} words
                                  </Typography>
                                )}
                                {story.tags && story.tags.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {story.tags.slice(0, 3).map((tag, index) => (
                                      <Chip 
                                        key={index}
                                        label={tag} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.75rem', height: 20 }}
                                      />
                                    ))}
                                    {story.tags.length > 3 && (
                                      <Typography variant="caption" color="text.secondary">
                                        +{story.tags.length - 3} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                              <Button 
                                variant="contained" 
                                size="small"
                                startIcon={<StoryIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/story/${story.story_id}`);
                                }}
                              >
                                Read
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}>
                    <StoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No submissions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Be the first to submit your story to this event!
                    </Typography>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Compact Event Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Event Information
              </Typography>

              {/* Horizontal Stats Bar */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                alignItems: 'center',
                mb: 2
              }}>
                {/* Date Range */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {formatDateRange(event.start_date, event.end_date)}
                  </Typography>
                </Box>

                {/* Time Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                    ⏰ {
                      event.status === 'active' ? getTimeUntilEnd(event.end_date) :
                      event.status === 'upcoming' ? getTimeUntilStart(event.start_date) :
                      event.status === 'closed' ? getTimeSinceEnd(event.end_date) :
                      event.status
                    }
                  </Typography>
                </Box>

                {/* Participants */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography 
                    variant="body2"
                    onClick={event.participant_count > 0 ? handleViewAllParticipants : undefined}
                    sx={{ 
                      cursor: event.participant_count > 0 ? 'pointer' : 'default',
                      '&:hover': event.participant_count > 0 ? {
                        textDecoration: 'underline',
                        color: 'primary.main'
                      } : {}
                    }}
                  >
                    {event.participant_count || 0} participants
                    {event.max_participants && ` (max ${event.max_participants})`}
                  </Typography>
                </Box>

                {/* Submissions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArticleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {event.submission_count || 0} submissions
                  </Typography>
                </Box>
              </Box>

              {/* Second Row - Requirements */}
              {(event.min_word_count || event.max_word_count || event.theme) && (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 3, 
                  alignItems: 'center',
                  mb: 2
                }}>
                  {/* Word Count */}
                  {(event.min_word_count || event.max_word_count) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        📏 {
                          event.min_word_count && event.max_word_count 
                            ? `${event.min_word_count}-${event.max_word_count} words`
                            : event.min_word_count 
                              ? `${event.min_word_count}+ words`
                              : `max ${event.max_word_count} words`
                        }
                      </Typography>
                    </Box>
                  )}

                  {/* Theme */}
                  {event.theme && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        🎯 {event.theme}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Action Button */}
              {isAuthenticated && event.status === 'active' && (
                <>
                  {event.user_participating ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<LeaveIcon />}
                      onClick={handleLeaveEvent}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Leaving...' : 'Leave Event'}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<JoinIcon />}
                      onClick={handleJoinEvent}
                      disabled={actionLoading || 
                        (event.max_participants && event.participant_count >= event.max_participants)}
                    >
                      {actionLoading ? 'Joining...' : 'Join Event'}
                    </Button>
                  )}
                </>
              )}

              {!isAuthenticated && event.status === 'active' && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<JoinIcon />}
                  onClick={() => setError('Please sign in to join events')}
                >
                  Sign In to Join
                </Button>
              )}

              {event.status !== 'active' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {event.status === 'upcoming' && 'Event hasn\'t started yet'}
                  {event.status === 'closed' && 'Event has ended'}
                  {event.status === 'cancelled' && 'Event has been cancelled'}
                </Alert>
              )}

              {/* View Results Button - Show for closed events with published results */}
              {event.status === 'closed' && event.results_published && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<TrophyIcon />}
                  onClick={() => navigate(`/events/${event.event_id}/results`)}
                  sx={{ mt: 2 }}
                >
                  View Contest Results
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Participants Card - Always show for authenticated users */}
          {isAuthenticated && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Participants ({event.participant_count || 0})
                </Typography>
                
                {participants.length > 0 ? (
                  <>
                    <List dense>
                      {participants.slice(0, 5).map((participant, index) => (
                        <React.Fragment key={participant.user_id}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                {getInitials(getParticipantDisplayName(participant))}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={getParticipantDisplayName(participant)}
                              secondary={`Joined ${new Date(participant.joined_at).toLocaleDateString()}`}
                            />
                          </ListItem>
                          {index < Math.min(participants.length - 1, 4) && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    {participants.length > 5 && (
                      <Box sx={{ textAlign: 'center', pt: 2 }}>
                        <Button 
                          variant="text" 
                          onClick={handleViewAllParticipants}
                          size="small"
                        >
                          +{participants.length - 5} more participants
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No participants yet. Be the first to join!
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* All Participants Modal */}
      <Dialog 
        open={participantsModalOpen} 
        onClose={() => setParticipantsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            <Typography variant="h6">
              Event Participants ({participants.length})
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setParticipantsModalOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {participantsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : participants && participants.length > 0 ? (
            <List>
              {participants.map((participant, index) => (
                <React.Fragment key={participant.user_id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                        {getInitials(getParticipantDisplayName(participant))}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getParticipantDisplayName(participant)}
                      secondary={`Joined ${new Date(participant.joined_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}`}
                    />
                  </ListItem>
                  {index < participants.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Participants Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Be the first to join this event!
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParticipantsModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetail;
